/**
 * Helpers for UniFi Operation Center audit facts.
 *
 * Producer facts are daily and idempotent (id = source|site|date). Hourly runs
 * overwrite the *same* day's fact, but prior days remain as separate documents.
 * Callers that treat "current health" must pick the latest auditDate per site
 * rather than the first array element Cosmos/API happens to return.
 */

/** True if candidate is newer than current (by auditDate, then lastUpdated). */
export function isNewerAuditRecord(candidate, current) {
  if (!current) return true;
  const cDate = candidate?.auditDate || '';
  const eDate = current?.auditDate || '';
  if (cDate !== eDate) return cDate > eDate;
  const cUpd = candidate?.lastUpdated || '';
  const eUpd = current?.lastUpdated || '';
  return cUpd > eUpd;
}

/**
 * Latest fact for one site among a list of audit records for a single auditRunId.
 * @param {Array} records
 * @param {string} siteId - UniFi site id (jobResults.target)
 * @returns {object|null}
 */
export function pickLatestForSite(records, siteId) {
  if (!Array.isArray(records) || !siteId) return null;
  let latest = null;
  for (const rec of records) {
    if (rec?.jobResults?.target !== siteId) continue;
    if (isNewerAuditRecord(rec, latest)) latest = rec;
  }
  return latest;
}

/**
 * Collapse a multi-day audit list to one record per (account, site).
 * Used by fleet-wide issue badges so yesterday's FAIL is not double-counted
 * with today's.
 * @param {Array} records
 * @returns {Array}
 */
export function latestRecordsPerSite(records) {
  if (!Array.isArray(records)) return [];
  const byKey = new Map();
  for (const rec of records) {
    const target = rec?.jobResults?.target;
    if (!target) continue;
    const key = `${rec.account || ''}|${target}`;
    const existing = byKey.get(key);
    if (isNewerAuditRecord(rec, existing)) byKey.set(key, rec);
  }
  return Array.from(byKey.values());
}
